import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { Card, cn } from "@clevis/shared-ui";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useAuth } from "../../hooks/useAuth";

// The migration hub: one card per source system. Clicking an available card
// opens that source's wizard.
const SOURCES = [
  {
    slug: "fieldpulse",
    name: "FieldPulse",
    description: "Customers, pricebook items, jobs and your team — straight from your FieldPulse account.",
    integration: "fieldpulse-import",
    href: "/migration/fieldpulse",
  },
  { slug: "jobber", name: "Jobber", description: "Coming soon.", integration: null, href: null },
  { slug: "housecall", name: "Housecall Pro", description: "Coming soon.", integration: null, href: null },
  { slug: "servicetitan", name: "ServiceTitan", description: "Coming soon.", integration: null, href: null },
  {
    slug: "csv",
    name: "Spreadsheet / CSV",
    description: "Coming soon — bring customers and price lists from any system via CSV.",
    integration: null,
    href: null,
  },
] as const;

export function MigrationHub() {
  const { hasIntegration } = useEntitlements();
  const { role } = useAuth();
  const isAdmin = role === "admin" || role === "owner";

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-lg font-bold">Migration</h1>
      <p className="text-xs text-steel">
        Switching to Clevis? Pick where your data lives today. Nothing in the source system is ever
        modified — migrations only read.
      </p>
      {isAdmin ? (
        <Link href="/migration/builders" className="block">
          <Card className="transition-colors hover:border-brandorange">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-ink">Clean up builder customers</span>
              <ArrowRight size={16} className="text-brandorange" />
            </div>
            <p className="mt-1 text-xs text-steel">
              One customer per builder, with a project per development: folds FieldPulse's "KB Homes" ×37 into one
              KB Homes and moves the jobs, estimates and invoices. Review first; undo anytime.
            </p>
          </Card>
        </Link>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        {SOURCES.map((s) => {
          const available = s.href !== null && (!s.integration || hasIntegration(s.integration));
          const card = (
            <Card
              className={cn(
                "flex h-full flex-col justify-between",
                available ? "transition-colors hover:border-brandorange" : "opacity-60",
              )}
            >
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-ink">{s.name}</span>
                  {available ? <ArrowRight size={16} className="text-brandorange" /> : null}
                </div>
                <p className="mt-1 text-xs text-steel">{s.description}</p>
              </div>
              {!available && s.href !== null ? (
                <p className="mt-2 text-[11px] text-steel">Ask Clevis support to enable this for your account.</p>
              ) : null}
            </Card>
          );
          return available && s.href ? (
            <Link key={s.slug} href={s.href} className="block">
              {card}
            </Link>
          ) : (
            <div key={s.slug}>{card}</div>
          );
        })}
      </div>
    </div>
  );
}
